// src/utils/schemas/escrita.ts
import { z } from "zod";
import { especieSchema, quillDeltaSchema, sexoSchema } from "./dominio";
import { phoneNumberSchema } from "./telefone";

// ─── [write] SCHEMAS DE ENTRADA (form → validação) ───────────────
// ─── Blocos primitivos (fonte única da verdade) ─────────────────────

// Constante de validação
const NAME_REGEX = /^[\p{L}\p{M}\s'-]+$/u; // Suporta acentos, letras e espaços

const DATE_ISO_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// "Hoje" no fuso LOCAL como YYYY-MM-DD — comparável lexicamente com datas ISO.
function hojeISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${dia}`;
}

const userNameSchema = z
  .string()
  .trim()
  .min(2, "O nome deve ter pelo menos 2 caracteres.")
  .max(50, "O nome é muito longo.")
  .regex(NAME_REGEX, "No nome use apenas letras, acentos e espaços.")
  .transform((val) => val.normalize("NFC"));

const userEmailSchema = z //ao editar o email existirá.
  .string()
  .trim()
  .max(100, "Email muito longo.")
  .transform((val) => val.toLowerCase())
  .refine((val) => !val.includes("+"), {
    message: "Emails com '+' não são permitidos.",
  })
  .pipe(z.email("Endereço de e-mail inválido."));

// ─── Bases (fonte única da verdade) ─────────────────────────────────

// Nomes de campo espelham a coluna de origem (ver "Convenção de nomenclatura
// de campos (EN/PT)" em docs/ARCHITECTURE.md): colunas do Better Auth em
// inglês (name, email, phoneNumber); colunas próprias do app em português
// (ex.: endereco, observacoes, nome_vacina). Não padronize o idioma sem migration.
// Objeto base de usuário
const userBase = z.object({
  name: userNameSchema,
  phoneNumber: phoneNumberSchema,
  email: userEmailSchema
    .optional() //o cadastro inicial é rápido, mas o usuário receberá email invisível: src/client/admin-user-create.ts
    .or(z.literal("")), //aceite também string vazia

  password: z
    .string()
    .trim()
    .min(6, "A senha deve ter pelo menos 6 caracteres.")
    .max(100, "A senha é muito longa.")
    .refine((val) => /\S/.test(val), "A senha não pode conter apenas espaços."),

  confirmPassword: z.string(),
});

// ─── Derivações por caso de uso ─────────────────────────────────────

// Criação administrativa de usuário
export const createUserSchema = userBase
  .extend({
    role: z.enum(["user", "admin"]).default("user"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não coincidem.",
    path: ["confirmPassword"],
  });

// Edição de usuário (campos do formulário de perfil)
// Campos em PT: espelham colunas próprias do app (ver "Convenção de nomenclatura de campos (EN/PT)").
export const editUserSchema = z.object({
  nome: userNameSchema,
  email: userEmailSchema,
  telefone: phoneNumberSchema.optional().or(z.literal("")),
  endereco: z.string().trim().optional().or(z.literal("")),
  observacoes: z
    .string()
    .trim()
    .max(500, "Máximo de 500 caracteres")
    .optional()
    .or(z.literal("")),
});

// Login
export const signinSchema = userBase.pick({
  phoneNumber: true,
  password: true,
});

// Cadastro de animal (triagem)
export const createAnimalSchema = z.object({
  nome: z.string().trim().min(1, "O nome do animal é obrigatório\n"),
  especie: especieSchema, // "cão" | "gato"
  sexo: sexoSchema, // "macho" | "fêmea"
  userId: z.string().min(1, "O nome do responsável é obrigatório"),
});

// Edição de animal (dados cadastrais). userId NÃO entra: o responsável não
// muda nesta tela. Campos espelham colunas PT do app.
export const editAnimalSchema = z
  .object({
    nome: z.string().trim().min(1, "O nome do animal é obrigatório"),
    especie: especieSchema,
    sexo: sexoSchema,
    raca: z.string().trim().optional().or(z.literal("")),
    microchip: z.string().trim().optional().or(z.literal("")),
    data_nascimento: z
      .string()
      .trim()
      .optional()
      .or(z.literal(""))
      .refine(
        (v) => !v || DATE_ISO_REGEX.test(v),
        "Formato de data inválido (use YYYY-MM-DD).",
      ),
    idade_estimada: z.coerce.number().int().min(0).max(1).optional(),
  })
  .superRefine((data, ctx) => {
    // Invariante: flag 1 ⇒ data preenchida (a estimativa gera a data).
    if (data.idade_estimada === 1 && !data.data_nascimento) {
      ctx.addIssue({
        code: "custom",
        path: ["data_nascimento"],
        message: "Data estimada exige a data calculada.",
      });
    }
    // Zod não valida data futura sozinho → comparação lexical de ISO.
    if (data.data_nascimento && data.data_nascimento > hojeISO()) {
      ctx.addIssue({
        code: "custom",
        path: ["data_nascimento"],
        message: "Data de nascimento não pode ser futura.",
      });
    }
  });

// ─── Blocos primitivos — vacina ─────────────────────────────────────

// Cadastro e edição de vacina
// Campos opcionais chegam do form como "" → normalizados para null (sentinela única de "sem informação").
// Caminhos de imagem: só caminho relativo servido por express.static (prefixo /img/).
const vaccineTextoSchema = z
  .string()
  .trim()
  .transform((val) => (val === "" ? null : val))
  .nullable();



// Presente → precisa ser ISO; vazio → já virou null.
const vaccineDataSchema = vaccineTextoSchema.refine(
  (val) => val === null || DATE_ISO_REGEX.test(val),
  "Formato de data inválido.",
);

// Caminhos permitidos: apenas as pastas de mídia de vacinas (catálogo controlado).
// A classe [a-z0-9-] exclui ".", "/" e "\", o que já barra path traversal
// (ex.: /img/vacinas/../../etc/passwd) sem precisar de checagem extra.
const VACCINE_IMAGEM_REGEX =
  /^\/img\/(vacinas|carimbo)\/[a-z0-9-]+\.(png|jpe?g|webp)$/;

const vaccineImagemSchema = vaccineTextoSchema.refine(
  (val) => val === null || VACCINE_IMAGEM_REGEX.test(val),
  "Caminho inválido: use /img/vacinas/... ou /img/carimbo/..., com extensão .png, .jpg ou .webp e nome em minúsculas.",
);

export const createVaccineSchema = z.object({
  nome_vacina: z.string().trim().min(1, "O nome da vacina é obrigatório."),
  laboratorio: vaccineTextoSchema,
  lote: vaccineTextoSchema,
  data_aplicacao: z
    .string()
    .trim()
    .min(1, "A data de aplicação é obrigatória.")
    .regex(DATE_ISO_REGEX, "Formato de data inválido."),
  data_validade: vaccineDataSchema,
  data_revacina: vaccineDataSchema,
  selo_imagem_url: vaccineImagemSchema,
  carimbo_imagem_url: vaccineImagemSchema,
});

// Edição de vacina (alias de createVaccineSchema)
export const editVaccineSchema = createVaccineSchema;
export type EditAnimalInput = z.infer<typeof editAnimalSchema>;

// ─── Ficha clínica (Quill) ──────────────────────────
export const CONTEUDO_MAX = 10_000_000; // ~10 MB → limite de segurança

export const clinicalRecordSchema = z.object({
  conteudo: z
    .string()
    .max(CONTEUDO_MAX)
    .refine(
      (s) => {
        try {
          quillDeltaSchema.parse(JSON.parse(s));
          return true;
        } catch {
          return false;
        }
      },
      { message: "O conteúdo não é um Delta válido." },
    ),
  version: z.coerce.number().int().min(0), // 👈 nova linha
});




// Exportação de tipos TypeScript inferidos
// Agrupados por verbo para facilitar a leitura.

// Criação
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type CreateAnimalInput = z.infer<typeof createAnimalSchema>;
export type CreateVaccineInput = z.infer<typeof createVaccineSchema>;

// Edição
export type EditUserInput = z.infer<typeof editUserSchema>;
export type EditVaccineInput = z.infer<typeof editVaccineSchema>;

// Sessão
export type SigninInput = z.infer<typeof signinSchema>;

export type ClinicalRecordInput = z.infer<typeof clinicalRecordSchema>;

